import React, { useEffect, useRef } from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { FormattedMessage } from "react-intl";
import { useAuth } from "contexts/AuthContext";
import { useLiveview } from "pages/Surveillance/Liveview/hooks/useLiveview";
import { useStartViewingCommand } from "pages/Surveillance/Liveview/hooks/useStartViewingCommand";
import { resolveStreamConfig } from "pages/Surveillance/Liveview/lib/streamConfig";

/**
 * Device drawer General tab — auto-play live preview with no control icons.
 * Reuses Liveview playback hooks (startViewing → viewer WS → MSE).
 */
const CameraLiveViewPanel = ({ auth, infoData }) => {
  const theme = useTheme();
  const { token: authToken } = useAuth();
  const videoRef = useRef(null);
  const autoStartedRef = useRef("");

  const deviceId = infoData?.macAddress || "";
  const offline = infoData?.linkStatus !== "online";
  const standalone = !auth?.orgs?.id || Object.keys(auth.orgs || {}).length === 0;
  const entId = standalone ? null : auth?.realm?.id || null;
  const orgId = standalone ? auth?.realm?.id : auth?.orgs?.id;
  const siteId = auth?.sites?.id || infoData?.siteId || "";
  const isAuthReady = Boolean(orgId);

  const { falconEndpoint, relayProtocol, accessToken } = resolveStreamConfig({
    device: infoData,
    authToken,
  });
  // Device drawer preview uses sub-stream for lower bandwidth; Liveview keeps streamId 1.
  const streamId = 2;

  const requestStartViewing = useStartViewingCommand({
    standalone,
    entId,
    orgId,
    siteId,
    isAuthReady,
  });

  const canStream = Boolean(deviceId && !offline && accessToken && isAuthReady);

  const { status, start } = useLiveview({
    videoRef,
    falconEndpoint: canStream ? falconEndpoint : "",
    relayProtocol,
    deviceId,
    deviceSiteId: siteId,
    accessToken: canStream ? accessToken : "",
    streamId,
    requestStartViewingCommand: canStream ? requestStartViewing : undefined,
    enabled: canStream,
    muted: false,
  });

  useEffect(() => {
    const key = canStream
      ? `${deviceId}:${accessToken}:${streamId}:${siteId}`
      : "";
    if (!key) {
      autoStartedRef.current = "";
      return undefined;
    }
    if (autoStartedRef.current === key) return undefined;
    autoStartedRef.current = key;

    let cancelRetry = () => {};
    const timer = setTimeout(() => {
      cancelRetry = start() || (() => {});
    }, 100);

    return () => {
      clearTimeout(timer);
      cancelRetry();
    };
  }, [canStream, deviceId, accessToken, streamId, siteId, start]);

  const hasError = canStream && status?.startsWith("Error");
  const isPlaying =
    canStream &&
    (status?.includes("playing") || status?.includes("connected"));
  const showLoading = canStream && !hasError && !isPlaying;

  return (
    <Box
      sx={{
        width: "100%",
        aspectRatio: "16 / 9",
        borderRadius: theme.shape.customBorderRadius[4],
        backgroundColor: theme.palette.primary.greyThird,
        border: `1px solid ${theme.palette.primary.greyThread}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {canStream && (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "contain",
            backgroundColor: "#000",
          }}
        />
      )}

      {offline && (
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 600,
            color: theme.palette.primary.white,
            zIndex: 1,
          }}
        >
          <FormattedMessage id="offline" defaultMessage="Offline" />
        </Typography>
      )}

      {!offline && showLoading && (
        <Box
          sx={{
            position: "relative",
            width: 64,
            height: 64,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              width: 48,
              height: 48,
              borderRadius: "50%",
              border: `5px solid ${theme.palette.primary.white}`,
              opacity: 0.9,
            }}
          />
          {Array.from({ length: 16 }).map((_, index) => {
            const rotate = index * 22.5;
            return (
              <Box
                key={rotate}
                sx={{
                  position: "absolute",
                  width: 5,
                  height: 5,
                  borderRadius: "50%",
                  backgroundColor: theme.palette.primary.white,
                  transform: `rotate(${rotate}deg) translateY(-34px)`,
                  transformOrigin: "center center",
                  opacity: 0.95,
                }}
              />
            );
          })}
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              backgroundColor: theme.palette.primary.white,
              zIndex: 1,
            }}
          />
        </Box>
      )}

      {hasError && (
        <Typography
          sx={{
            position: "absolute",
            bottom: 8,
            left: 8,
            right: 8,
            fontSize: 11,
            color: "rgba(255,255,255,0.75)",
            textAlign: "center",
            zIndex: 1,
          }}
        >
          {status}
        </Typography>
      )}
    </Box>
  );
};

export default CameraLiveViewPanel;
